import { labelOf, PAY_MODALITIES, WORKER_ID_TYPES, type WorkerDetail } from '@facture/contracts'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import {
  ArrowLeftIcon,
  ArrowUpRightIcon,
  BanknoteIcon,
  CalendarIcon,
  HardHatIcon,
  MailIcon,
  MoreHorizontalIcon,
  PencilIcon,
  PhoneIcon,
  Trash2Icon,
} from 'lucide-react'
import { type ReactNode, useState } from 'react'
import { toast } from 'sonner'
import { AnimatedNumber, FadeIn, Stagger, StaggerItem } from '@/components/motion'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { confirm } from '@/components/ui/confirm'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Avatar } from '@/components/ui/misc'
import { EmptyState, Page } from '@/components/ui/page'
import { Skeleton } from '@/components/ui/skeleton'
import { invalidatePayroll, workerQuery } from '@/features/payroll/api'
import {
  fmtCol,
  fmtHours,
  JornadaFlag,
  kindLabel,
  Over12Flag,
  PayrollDisclaimer,
  punchLabel,
  rateText,
  restDayName,
  SurchargeFlag,
} from '@/features/payroll/payroll-ui'
import { WorkerSheet } from '@/features/payroll/worker-sheet'
import { api, errorMessage } from '@/lib/api'
import { formatDate, formatIdentification } from '@/lib/format'
import { useCan } from '@/lib/session'
import { cn } from '@/lib/utils'

export const Route = createFileRoute('/_app/projects/workers/$id')({
  staticData: { crumb: 'Trabajador' },
  component: WorkerPage,
})

const DEDUCTIONS = ['advance', 'settlement']

function WorkerPage() {
  const { id } = Route.useParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const canWrite = useCan('payroll:write')
  const { data: worker, error, isLoading } = useQuery(workerQuery(id))
  const [editing, setEditing] = useState(false)

  if (error) {
    return (
      <Page>
        <p className="text-muted-foreground text-sm">{errorMessage(error)}</p>
      </Page>
    )
  }
  if (isLoading || !worker) {
    return (
      <Page>
        <Skeleton className="mb-6 h-36 rounded-2xl" />
        <div className="mb-6 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          {[0, 1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-28 rounded-xl" />
          ))}
        </div>
        <Skeleton className="h-80 rounded-xl" />
      </Page>
    )
  }

  const remove = () =>
    confirm({
      title: worker.assignments.length ? '¿Desactivar este trabajador?' : '¿Eliminar este trabajador?',
      description: worker.assignments.length
        ? 'Tiene asignaciones: se desactiva junto con ellas y conserva su historia y su saldo.'
        : 'No tiene asignaciones ni horas registradas.',
      confirmLabel: worker.assignments.length ? 'Desactivar' : 'Eliminar',
      destructive: true,
      onConfirm: async () => {
        const result = await api.delete<{ deleted: boolean }>(`/workers/${id}`)
        invalidatePayroll(queryClient)
        if (result.deleted) {
          toast.success('Trabajador eliminado')
          navigate({ to: '/projects/workers' })
        } else toast.success('Trabajador desactivado')
      },
    })

  const t = worker.totals
  return (
    <Page>
      <div className="relative mb-6 overflow-hidden rounded-2xl border border-border bg-card shadow-elevation-1">
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 bg-linear-to-br from-warning/12 via-warning/4 to-transparent"
        />
        <div className="relative flex flex-col gap-5 p-5 sm:p-6 lg:flex-row lg:items-start lg:justify-between">
          <div className="flex min-w-0 items-start gap-4">
            <Button asChild variant="ghost" size="icon-sm" className="mt-1 shrink-0">
              <Link to="/projects/workers" aria-label="Volver a trabajadores">
                <ArrowLeftIcon />
              </Link>
            </Button>
            <Avatar name={worker.name} className="hidden size-14 text-lg ring-4 ring-card sm:flex" />
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <h1 className="min-w-0 break-words font-semibold text-xl tracking-tight sm:text-2xl">
                  {worker.name}
                </h1>
                <Badge variant={worker.isActive ? 'success' : 'neutral'} dot>
                  {worker.isActive ? 'Activo' : 'Inactivo'}
                </Badge>
              </div>
              <div className="mt-1 text-muted-foreground text-sm">
                {labelOf(WORKER_ID_TYPES, worker.identificationType)}{' '}
                <span className="font-mono">
                  {worker.identification
                    ? formatIdentification(worker.identificationType, worker.identification)
                    : 'sin número'}
                </span>
                {worker.defaultPositionName && <> · {worker.defaultPositionName}</>}
              </div>
              <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-1.5 text-muted-foreground text-sm">
                {worker.phone && (
                  <a href={`tel:${worker.phone}`} className="flex items-center gap-1.5 hover:text-foreground">
                    <PhoneIcon className="size-3.5" /> {worker.phone}
                  </a>
                )}
                {worker.email && (
                  <a
                    href={`mailto:${worker.email}`}
                    className="flex items-center gap-1.5 hover:text-foreground"
                  >
                    <MailIcon className="size-3.5" /> {worker.email}
                  </a>
                )}
                {(worker.bankName || worker.iban) && (
                  <span className="flex min-w-0 items-center gap-1.5">
                    <BanknoteIcon className="size-3.5 shrink-0" />
                    {worker.bankName}
                    {worker.iban && <span className="truncate font-mono text-xs">{worker.iban}</span>}
                  </span>
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 sm:gap-3">
            <div className="rounded-xl border border-border bg-card/70 px-4 py-2 text-right backdrop-blur max-sm:flex-1">
              <div className="text-muted-foreground text-xs">Saldo por pagar</div>
              <div className={cn('font-semibold text-lg tabular', t.balance < 0 && 'text-destructive')}>
                <AnimatedNumber value={t.balance} format={(n) => fmtCol(Math.round(n))} />
              </div>
            </div>
            {canWrite && (
              <>
                <Button variant="outline" onClick={() => setEditing(true)}>
                  <PencilIcon /> <span className="max-sm:sr-only">Editar</span>
                </Button>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="icon" aria-label="Más acciones">
                      <MoreHorizontalIcon />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem destructive onSelect={remove}>
                      <Trash2Icon /> {worker.assignments.length ? 'Desactivar' : 'Eliminar'}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            )}
          </div>
        </div>
      </div>

      <FadeIn className="grid gap-6">
        <Stagger className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          <Metric
            label="Devengado"
            value={t.earned}
            hint={`${fmtHours(t.ordinaryHours)} ordinarias · ${fmtHours(t.extraHours)} extra`}
          />
          <Metric label="Dietas y transporte" value={t.allowances} hint="Viáticos registrados" />
          <Metric
            label="Adelantos y liquidaciones"
            value={t.advances + t.settlements}
            hint={t.settlements ? `${fmtCol(t.settlements)} liquidado` : 'Pagos ya entregados'}
          />
          <Metric label="Saldo por pagar" value={t.balance} accent hint="Devengado + viáticos − pagos" />
        </Stagger>

        <PayrollDisclaimer />

        <Card>
          <CardHeader>
            <div>
              <CardTitle>Proyectos</CardTitle>
              <CardDescription>
                Asignaciones del trabajador con su tarifa, su descanso y su saldo.
              </CardDescription>
            </div>
          </CardHeader>
          <CardContent className="grid gap-2">
            {worker.assignments.length === 0 ? (
              <EmptyState
                icon={HardHatIcon}
                title="Sin proyectos"
                description="Asignalo desde la pestaña Personal de un proyecto."
                className="py-8"
              />
            ) : (
              worker.assignments.map((a) => <AssignmentRow key={a.id} a={a} />)
            )}
          </CardContent>
        </Card>

        <Card className="overflow-hidden">
          <CardHeader className="pb-4">
            <div>
              <CardTitle>Movimientos recientes</CardTitle>
              <CardDescription>Las últimas 30 entradas en todos sus proyectos.</CardDescription>
            </div>
          </CardHeader>
          {worker.recentEntries.length === 0 ? (
            <p className="px-5 pb-6 text-muted-foreground text-sm">
              Todavía no tiene horas ni montos registrados.
            </p>
          ) : (
            <ul className="divide-y divide-border border-border border-t">
              {worker.recentEntries.map((e) => {
                const negative = DEDUCTIONS.includes(e.kind)
                return (
                  <li key={e.id} className="flex items-center gap-4 px-5 py-3">
                    <div className="w-16 shrink-0 text-sm">
                      <div className="font-medium tabular">{formatDate(e.date, 'd MMM')}</div>
                      <div className="text-muted-foreground text-xs">{formatDate(e.date, 'EEEE')}</div>
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-1.5 text-sm">
                        <span className="font-medium">{kindLabel(e.kind)}</span>
                        {e.kind === 'work' && (
                          <span className="text-muted-foreground tabular">
                            {punchLabel(e, false)} · {fmtHours(e.quantity)}
                          </span>
                        )}
                        {e.kind === 'overtime' && (
                          <span className="text-muted-foreground">{fmtHours(e.quantity)}</span>
                        )}
                        <JornadaFlag type={e.jornadaType} compact />
                        <SurchargeFlag day={e.surchargeDay} mult={e.surchargeMult} compact />
                        {e.warnings.some((w) => w.code === 'JORNADA_SOBRE_12H') && <Over12Flag compact />}
                      </div>
                      <div className="truncate text-muted-foreground text-xs">
                        {e.projectCode ? `${e.projectCode} · ` : ''}
                        {e.projectName}
                        {e.notes ? ` · ${e.notes}` : ''}
                      </div>
                    </div>
                    <div
                      className={cn(
                        'shrink-0 text-right font-medium text-sm tabular',
                        negative && 'text-muted-foreground',
                      )}
                    >
                      {negative ? '−' : ''}
                      {fmtCol(e.totalAmount)}
                    </div>
                  </li>
                )
              })}
            </ul>
          )}
        </Card>
      </FadeIn>
      <WorkerSheet open={editing} worker={worker} onOpenChange={setEditing} />
    </Page>
  )
}

function AssignmentRow({ a }: { a: WorkerDetail['assignments'][number] }) {
  return (
    <Link
      to="/projects/$id"
      params={{ id: a.projectId }}
      search={{ tab: 'personal' }}
      className={cn(
        'group grid gap-3 rounded-xl border border-border px-4 py-3 transition-colors hover:bg-accent/50 sm:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)_auto] sm:items-center',
        !a.isActive && 'opacity-60',
      )}
    >
      <div className="flex min-w-0 items-center gap-3">
        <span className="flex size-9 shrink-0 items-center justify-center rounded-xl bg-warning-soft text-warning-soft-foreground">
          <HardHatIcon className="size-4" />
        </span>
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="truncate font-medium text-sm">{a.projectName}</span>
            {a.projectCode && (
              <span className="shrink-0 font-mono text-muted-foreground text-xs">{a.projectCode}</span>
            )}
            {!a.isActive && <Badge variant="neutral">Inactiva</Badge>}
          </div>
          <div className="truncate text-muted-foreground text-xs">
            {a.positionName} · {labelOf(PAY_MODALITIES, a.modality)}{' '}
            {rateText(a.modality, a.rate, a.monthlySalary)} · descanso {restDayName(a.restDay).toLowerCase()}
          </div>
        </div>
      </div>
      <div className="flex flex-wrap gap-x-4 gap-y-1 text-muted-foreground text-xs tabular">
        <span>
          Devengado <strong className="font-medium text-foreground">{fmtCol(a.totals.earned)}</strong>
        </span>
        <span className="flex items-center gap-1">
          <CalendarIcon className="size-3" />
          {a.lastEntryDate ? formatDate(a.lastEntryDate, 'd MMM yy') : 'sin registros'}
        </span>
      </div>
      <div className="flex items-center justify-between gap-3 sm:justify-end">
        <span className="text-right">
          <span className="block text-muted-foreground text-xs">Saldo</span>
          <span className="font-semibold text-sm tabular">{fmtCol(a.totals.balance)}</span>
        </span>
        <ArrowUpRightIcon className="size-4 text-muted-foreground transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
      </div>
    </Link>
  )
}

function Metric({
  label,
  value,
  hint,
  accent,
}: {
  label: string
  value: number
  hint?: ReactNode
  accent?: boolean
}) {
  return (
    <StaggerItem>
      <Card className="relative h-full overflow-hidden p-5">
        {accent && (
          <div className="pointer-events-none absolute -top-16 -right-10 size-44 rounded-full bg-warning/20 blur-3xl" />
        )}
        <div className="relative">
          <div className="text-muted-foreground text-sm">{label}</div>
          <div className="mt-2 truncate font-semibold text-[26px] tracking-tight tabular">
            <AnimatedNumber value={value} format={(n) => fmtCol(Math.round(n))} />
          </div>
          {hint && <div className="mt-1 text-muted-foreground text-xs">{hint}</div>}
        </div>
      </Card>
    </StaggerItem>
  )
}
